import React, { useEffect, useState } from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger
} from '../ui/tabs';
import { Button } from '../ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger
} from '../ui/collapsible';

interface CourseContentTabsProps {
    modules: any[];
    course: any;
    onWatchVideo: (video: any) => void;
}

export const CourseContentTabs: React.FC<CourseContentTabsProps> = ({
    modules,
    course,
    onWatchVideo
}) => {

    const [modulesData ,setModules] = useState(modules ?? []);
    useEffect(()=>{
        setModules(eliminateDuplicates(modules));
    },[]);


    const eliminateDuplicates = (input)=>{
        let unique=[];
        input.forEach(x=>{
            if(!unique.filter(m=>m.id==x.id)) unique.push(x);
        });
        return unique;
    }
    return (
        <Tabs defaultValue="content" className="mt-12">
            <TabsList>
                <TabsTrigger value="content">Course Content</TabsTrigger>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="discussion">Discussion</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="pt-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-xl font-semibold mb-4">All Lessons</h2>
                    {modulesData?.length > 0 ? (
                        <div className="space-y-6">
                            {modulesData?.map((module) => (
                                <Collapsible key={module.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                    <CollapsibleTrigger className="flex justify-between items-center w-full p-4 bg-gray-50 hover:bg-gray-100">
                                        <div className="flex items-center gap-2">
                                            {module?.batches?.filter((l: any) => l?.isCompleted)?.length ? (
                                                <CheckCircle2 size={18} className="text-green-500" />
                                            ) : (
                                                <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                            )}
                                            <span className="font-medium">{module?.moduleName}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-500">
                                                {module?.batches?.filter((l: any) => l?.isCompleted)?.length || 0}/{module?.batches?.length} lessons
                                            </span>
                                            <ChevronDown size={18} className="text-gray-500" />
                                        </div>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        {module?.moduleTopics && module?.moduleTopics?.topic?.length > 0 ? (
                                            <ul className="divide-y divide-gray-100">
                                                {module?.moduleTopics?.topic.map((topic: any) => (
                                                    <li key={topic?.id || `lesson-${Math.random()}`} className="p-2 flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            {module?.batches?.filter((l: any) => l?.isCompleted)?.length ? (
                                                                <CheckCircle2 size={18} className="text-green-500" />
                                                            ) : (
                                                                <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                                            )}
                                                            <div>
                                                                <p className="text-sm text-gray-400 leading-4 font-medium">{topic}</p>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            size="xs"
                                                            variant="ghost"
                                                            className="text-primary hover:text-primary hover:bg-gray-50 py-1 px-2"
                                                        >
                                                            {module?.batches?.filter((l: any) => l?.isCompleted)?.length ? 'Review' : 'Start'}
                                                        </Button>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">
                                                No lessons available for this module.
                                            </div>
                                        )}
                                        {module?.moduleTopics && module?.moduleTopics.length > 0 ? (
                                            <ul className="divide-y divide-gray-100">
                                                {module.moduleTopics.map((moduleTopic: any) => (
                                                    <CollapsibleContent key={moduleTopic?.id || `module-${Math.random()}`}>
                                                        <div className="flex items-center p-2">
                                                            <h4 className="font-medium text-gray-700">{moduleTopic.title}</h4> {/* Assuming moduleTopic has a title */}
                                                        </div>
                                                        {moduleTopic?.topic && moduleTopic.topic.length > 0 ? (
                                                            <ul className="divide-y divide-gray-100">
                                                                {moduleTopic.topic.map((topic: any) => (
                                                                    <li key={topic?.id || `lesson-${Math.random()}`} className="p-2 flex items-center justify-between">
                                                                        <div className="flex items-center gap-3">
                                                                            {module?.batches?.filter((l: any) => l?.isCompleted)?.length ? (
                                                                                <CheckCircle2 size={18} className="text-green-500" />
                                                                            ) : (
                                                                                <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                                                            )}
                                                                            <div>
                                                                                <p className="text-sm text-gray-400 leading-4 font-medium">{topic}</p> {/* Use topic.title assuming it has a title property */}
                                                                            </div>
                                                                        </div>
                                                                        <Button
                                                                            size="xs"
                                                                            variant="ghost"
                                                                            className="text-primary hover:text-primary hover:bg-gray-50 py-1 px-2"
                                                                        >
                                                                            {module?.batches?.filter((l: any) => l?.isCompleted)?.length ? 'Review' : 'Start'}
                                                                        </Button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <div className="p-4 text-center text-gray-500">
                                                                No topics available for this module.
                                                            </div>
                                                        )}
                                                    </CollapsibleContent>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="p-4 text-center text-gray-500">
                                                No modules available.
                                            </div>
                                        )}
                                    </CollapsibleContent>
                                </Collapsible>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <p className="text-gray-500">No content available for this course yet.</p>
                        </div>
                    )}
                </div>
            </TabsContent>

            <TabsContent value="overview" className="pt-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-xl font-semibold mb-4">Course Overview</h2>
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-medium mb-2">About this course</h3>
                            <p className="text-gray-700">
                                {course.longDescription || course.description}
                            </p>
                        </div>

                        {course.learningOutcomes && course.learningOutcomes.length > 0 ? (
                            <div>
                                <h3 className="text-lg font-medium mb-2">What you'll learn</h3>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {course.learningOutcomes.map((item: string, index: number) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <CheckCircle2 size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-lg font-medium mb-2">What you'll learn</h3>
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {[
                                        "Programming fundamentals and advanced concepts",
                                        "Web development with HTML, CSS, and JavaScript",
                                        "Frontend development with React",
                                        "Backend development frameworks",
                                        "Database design and integration",
                                        "RESTful API development",
                                        "Authentication and authorization",
                                        "Deployment and DevOps basics",
                                        "Project structure and best practices",
                                        "Building real-world applications"
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <CheckCircle2 size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div>
                            <h3 className="text-lg font-medium mb-2">Prerequisites</h3>
                            <ul className="space-y-2">
                                {course.prerequisites && course.prerequisites.length > 0 ? (
                                    course.prerequisites.map((prereq: string, index: number) => (
                                        <li key={index} className="flex items-start gap-2">
                                            <span className="text-primary">•</span>
                                            <span>{prereq}</span>
                                        </li>
                                    ))
                                ) : (
                                    <>
                                        <li className="flex items-start gap-2">
                                            <span className="text-primary">•</span>
                                            <span>Basic computer skills and familiarity with using the internet</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-primary">•</span>
                                            <span>No prior programming experience required, but helpful</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-primary">•</span>
                                            <span>Willingness to learn and practice regularly</span>
                                        </li>
                                    </>
                                )}
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-lg font-medium mb-2">Instructor</h3>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                                    <span className="text-gray-600 font-medium">
                                        {course.instructor ? course.instructor.charAt(0) : "I"}
                                    </span>
                                </div>
                                <div>
                                    <h4 className="font-medium">{course.instructor || "Course Instructor"}</h4>
                                    <p className="text-sm text-gray-600">{course.instructorTitle || "Expert Educator"}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </TabsContent>

            <TabsContent value="discussion" className="pt-6">
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h2 className="text-xl font-semibold mb-4">Course Discussion</h2>
                    <p className="text-gray-700">
                        This is where you can discuss course content with fellow students and instructors. Ask questions, share insights, and collaborate on projects.
                    </p>
                    <div className="mt-6 text-center py-6">
                        <p className="text-gray-500">No discussions yet. Be the first to post!</p>
                        <Button className="mt-4 bg-primary hover:bg-primary/90">
                            Start a Discussion
                        </Button>
                    </div>
                </div>
            </TabsContent>
        </Tabs>
    );
};